/**
 * Input Component
 * @author Muhammad Arsan
 * @description A reusable input component using Material-UI's Input component
 */
/**
 * Input component for Form fields
 * @param {InputProps} props - The component props
 * @param {number} [props.label=SPINNER_DEFAULTS.SIZE] - The label of the field
 * @param {string} [props.iconVisibleSrc=SPINNER_DEFAULTS.COLOR] - The visible icon of the field
 * @param {number} [props.iconHiddenSrc=SPINNER_DEFAULTS.THICKNESS] - The hidden icon of the field
 * @returns {JSX.Element} The rendered Input component
 */

import React, { useEffect, useRef } from "react";
import { TextField, InputAdornment } from "@mui/material";
import { FORM_PROPERTIES } from "../contants";
import type { InputFieldProps } from "../types/inputfield";
import { TextFieldStyles } from "../styles/TextField.styles";

/**
 * A reusable input component for forms, built on MUI's TextField.
 * @author Muhammad Arsan
 */
const InputField: React.FC<InputFieldProps> = ({
	id,
	label,
	labelClassName,
	type = "text",
	placeholder,
	value,
	onChange,
	disabled = false,
	error = false,
	helperText = "",
	startIcon,
	endIcon,
	multiline = false,
	rows,
	className,
	onClick,
	isClickable,
	"aria-label": ariaLabel,
	"aria-describedby": ariaDescribedby,
	required,
	size = "medium",
	surfaceVariant = "default",
	InputLabelProps,
	...rest
}) => {
	const inputRef = useRef<HTMLInputElement>(null);

	// Update date input colors only for date type
	useEffect(() => {
		if (type === "date" && inputRef.current) {
			const input = inputRef.current;
			if (value && value.toString().trim() !== "") {
				// Has value - dark color
				input.style.color = "#000";
				input.style.webkitTextFillColor = "#000";
			} else {
				// No value - light gray for placeholder
				input.style.color = "#6B7280";
				input.style.webkitTextFillColor = "#6B7280";
			}
		}
	}, [type, value]);
	return (
		<div className="min-w-0 max-w-full">
			{label && (
				<label
					className={`mb-2 block text-sm sm:text-base ${labelClassName ?? "font-medium text-foreground"} ${surfaceVariant === "auth" ? "text-gray-700" : ""}`.trim()}
					htmlFor={id}>
					{label}
					{required && (
						<>
							{" "}
							<span className="text-red-500">{FORM_PROPERTIES.REQUIRED_ASTERIK}</span>
						</>
					)}
				</label>
			)}
			<TextField
				id={id}
				className={`${className ?? ""} ${isClickable ? "cursor-pointer" : ""}`}
				fullWidth
				variant="outlined"
				type={type}
				placeholder={placeholder}
				value={value}
				onChange={onChange}
				onClick={onClick}
				disabled={disabled}
				required={required}
				error={error}
				helperText={helperText}
				rows={rows}
				multiline={multiline}
				aria-label={ariaLabel || (typeof label === "string" ? label : undefined)}
				aria-describedby={ariaDescribedby}
				aria-invalid={error ? "true" : "false"}
				sx={TextFieldStyles(isClickable, size, error, disabled, surfaceVariant)}
				InputLabelProps={InputLabelProps}
				InputProps={{
					startAdornment: startIcon ? (
						<InputAdornment
							position="start"
							sx={{
								marginRight: -1,
								"& .MuiInputAdornment-positionStart": {
									marginRight: 0,
								},
							}}>
							{startIcon}
						</InputAdornment>
					) : undefined,
					endAdornment: endIcon ? <InputAdornment position="end">{endIcon}</InputAdornment> : undefined,
				}}
				{...rest}
			/>
		</div>
	);
};

export default InputField;
